import type { Pixel, Unit } from '@/data';
import type { Image3DPageData } from '@/data/sources';
import Tab from '@/data/Tab';
import type { MetadataFormat } from '@/data/Metadata';
import type { ImageDimensions } from './SourceParser';
import MetadataParser from '../MetadataParser';

/**
  * An audio file object
  * Contains the relative path to an audio file
  * Contains a time conversion object
  * May contain wave image file objects
  */
export interface TabFileFormat extends MetadataFormat {
  /** The score file url */
  tab?: string;
  /** The license associated with this track, if any */
  license?: string;
  id?: string;
}

export function tabFormatFromNewFormat(sourceFormat : Map<string, unknown>)
  : TabFileFormat {
  const refs : Record<string, string> = {};

  (Array.from(sourceFormat.keys()) as string[]).filter((key) => key.startsWith('ref')).forEach((refKey) => { refs[refKey] = sourceFormat.get(refKey) as string; });

  return {
    tab: sourceFormat.get('tab') as string,
    info: sourceFormat.get('text') as string,
    license: sourceFormat.get('license') as string,
    contributors: sourceFormat.get('contributors') as Record<string, string>,
    refs,
    id: sourceFormat.get('id') as string,
    year: sourceFormat.get('year') as string,
  };
}

/**
 * An image file object
 *
 * Contains:
 * * the relative path to an image file
 * * a time conversion object
 *
 * For score images, the conversion should be onset -> pixel
 *
 * For wave images, the conversion should be second -> pixel
 */

export default class TabParser extends Tab {
  static fromFormat(
    format : TabFileFormat,
    tab: string,
    svgImage:string,
    pixelUnit: Unit<Pixel>,
    dim: ImageDimensions,
    pages : Image3DPageData[],
    isLinear = false,
  ): Tab {
    if (!tab) throw new Error('No source file or id was provided');
    const metadata = MetadataParser.fromFormat(format);

    return new Tab({
      scoreUrl: tab,
      svgImage,
      pixelUnit,
      height: dim.height,
      width: dim.width,
      pages,
      isLinear,
      metadata,
    });
  }
}
